import {
  ApolloError,
  DocumentNode,
  MutationHookOptions,
  useMutation
} from '@apollo/client';
import { GraphQLError } from 'graphql';
import useNotification from 'Hooks/useNotification';
import * as React from 'react';

interface Props<TData, TVariables> {
  mutation: DocumentNode;
  // GraphQL error code identifying a partial batch failure for this mutation.
  batchErrorCode: string;
  refetchQueries: MutationHookOptions<TData, TVariables>['refetchQueries'];
  selectedUserCount: number;
  onCollapse: () => void;
  onReloadUsers: () => void;
  getVariables: () => TVariables;
}

// Shared submit/error handling for the "remove users" mutations (account and guest users link).
const useRemoveUsersMutation = <TData, TVariables>({
  mutation,
  batchErrorCode,
  refetchQueries,
  selectedUserCount,
  onCollapse,
  onReloadUsers,
  getVariables
}: Props<TData, TVariables>) => {
  const { onError, onSuccess } = useNotification({ domain: 'user' });
  const [pendingUsersCount, setPendingUsersCount] = React.useState(0);

  const onCompleted = () => {
    onSuccess('page.users.action.delete.success', { count: selectedUserCount });
    onCollapse();
    onReloadUsers();
  };

  const onMutationError = (error: ApolloError) => {
    onError(error);
    onCollapse();

    if (error.graphQLErrors == null) {
      return;
    }

    /*
     * The delete users mutation might partially fail (and thus partially succeed).
     * In this situation, users should be reloaded as some of the selected users are gone.
     */
    const batchError = error.graphQLErrors
      .map(graphQLError => graphQLError.extensions)
      .find(apiError => apiError?.code === batchErrorCode);
    if (
      batchError?.innerErrors &&
      // If there are less errors than the number of users to delete,
      // then it's a partial success
      (batchError.innerErrors as Array<GraphQLError>).length < pendingUsersCount
    ) {
      onReloadUsers();
    }
  };

  const [onRemoveUsers, { loading }] = useMutation<TData, TVariables>(mutation, {
    onError: onMutationError,
    onCompleted,
    refetchQueries
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    onRemoveUsers({ variables: getVariables() });

    setPendingUsersCount(selectedUserCount);
  };

  return { loading, onSubmit };
};

export default useRemoveUsersMutation;
